import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, App, theme, Select, Spin } from 'antd';
import { LockOutlined, UserOutlined, BankOutlined } from '@ant-design/icons';
import { useAuthStore } from '@/modules/auth/authStore';
import { useAppStore } from '@/stores/appStore';
import { gradients, shadows } from '@/theme';
import type { LoginRequest, LoginResponse } from '@/types/api';
import api from '@/services/api';

const { Title, Text } = Typography;

// Deteksi mode Desktop vs SaaS
const isDesktopMode = import.meta.env.VITE_MODE === 'Desktop';

interface CompanyListItem {
  code: string;
  name: string;
}

export default function LoginPage() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<CompanyListItem[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const { token } = theme.useToken();
  // Gunakan App.useApp() untuk message agar bisa mengakses theme context
  const { message } = App.useApp();

  // Fetch companies untuk mode Desktop
  useEffect(() => {
    if (isDesktopMode) {
      const fetchCompanies = async () => {
        setLoadingCompanies(true);
        try {
          const response = await api.get<CompanyListItem[]>('/auth/companies');
          setCompanies(response.data);

          // Jika hanya ada 1 perusahaan, auto-select
          if (response.data.length === 1) {
            form.setFieldValue('tenantCode', response.data[0].code);
          }
        } catch (error) {
          console.error('Failed to fetch companies:', error);
          message.error('Gagal memuat daftar perusahaan');
        } finally {
          setLoadingCompanies(false);
        }
      };
      fetchCompanies();
    }
  }, [form]);

  const onFinish = async (values: LoginRequest) => {
    setLoading(true);
    try {
      const response = await api.post<LoginResponse>('/auth/login', values);
      const { accessToken, refreshToken, user, tenant, tahunBukuAktif, tahunBukuKemarin, jenisTahunBuku, statusBuku } = response.data;

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      login(user, tenant);

      // Simpan data tahun buku ke appStore
      useAppStore.getState().setAppInfo({
        TahunBukuAktif: tahunBukuAktif,
        TahunBukuKemarin: tahunBukuKemarin,
        JenisTahunBuku: jenisTahunBuku as 'NORMAL' | 'LAMPAU',
        StatusBuku: statusBuku as 'OPEN' | 'CLOSED',
      });

      message.success('Login berhasil!');
      navigate('/');
    } catch {
      message.error('Login gagal. Periksa kembali username dan password.');
    } finally {
      setLoading(false);
    }
  };

  // Render field perusahaan berdasarkan mode
  const renderCompanyField = () => {
    if (!isDesktopMode) {
      // Mode SaaS: Textbox kode perusahaan
      return (
        <Form.Item
          name="tenantCode"
          rules={[{ required: true, message: 'Masukkan kode perusahaan' }]}
        >
          <Input prefix={<BankOutlined />} placeholder="Kode Perusahaan" />
        </Form.Item>
      );
    }

    // Mode Desktop
    if (loadingCompanies) {
      return (
        <div style={{ textAlign: 'center', padding: '12px 0' }}>
          <Spin size="small" />
          <Text type="secondary" style={{ marginLeft: 8 }}>Memuat daftar perusahaan...</Text>
        </div>
      );
    }

    // Jika hanya 1 perusahaan, tidak perlu tampilkan dropdown
    if (companies.length === 1) {
      return (
        <Form.Item name="tenantCode" hidden>
          <Input />
        </Form.Item>
      );
    }

    // Jika > 1 perusahaan, tampilkan dropdown
    if (companies.length > 1) {
      return (
        <Form.Item
          name="tenantCode"
          rules={[{ required: true, message: 'Pilih perusahaan' }]}
        >
          <Select
            placeholder="Pilih Perusahaan"
            size="large"
            suffixIcon={<BankOutlined />}
            options={companies.map(c => ({
              value: c.code,
              label: c.name,
            }))}
          />
        </Form.Item>
      );
    }

    // Tidak ada perusahaan
    return (
      <div style={{ textAlign: 'center', padding: '12px 0' }}>
        <Text type="danger">Tidak ada perusahaan terdaftar</Text>
      </div>
    );
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: gradients.primaryGradient,
        padding: 24,
      }}
    >
      <Card style={{ width: 400, boxShadow: shadows.lg }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2} style={{ color: token.colorPrimary, marginBottom: 8 }}>
            Booku
          </Title>
          <Text type="secondary">Sistem Akuntansi Terpadu</Text>
        </div>

        <Form
          form={form}
          name="login"
          onFinish={onFinish}
          layout="vertical"
          size="large"
          requiredMark={false}
        >
          {renderCompanyField()}

          <Form.Item
            name="username"
            rules={[{ required: true, message: 'Masukkan username' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="Username" />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: 'Masukkan password' }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Password" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              style={{ height: 44 }}
              disabled={isDesktopMode && companies.length === 0 && !loadingCompanies}
            >
              Masuk
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
